import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/streamApi';
import useSpeak from '../utils/useSpeak';
import { announcePauseChange, fmtRemaining } from '../utils/devicesPause';
import '../css/dashboard.css';

// Play Devices / Pause Devices — one implementation for the mobile Dashboard's
// bottom sheets and the desktop "Play & Pause Devices" page, so a fix lands in both.

const PAUSE_DURATIONS = [
  { key: '5m',  label: '5 min'   },
  { key: '15m', label: '15 min'  },
  { key: '30m', label: '30 min'  },
  { key: '1h',  label: '1 hr'    },
  { key: '2h',  label: '2 hr'    },
  { key: '6h',  label: '6 hr'    },
  { key: '12h', label: '12 hr'   },
  { key: '24h', label: '24 hr'   },
  { key: '1w',  label: '1 week'  },
  { key: '1mo', label: '1 month' },
  { key: '6mo', label: '6 months'},
];



const RELAY_TYPE_ICON = { light: '💡', siren: '🔔', motor: '⚙️', onoff: '🔌' };

export function PlayDevicesSection() {
  const [sirenOn,    setSirenOn]    = useState(false);
  const [channels,   setChannels]   = useState([]);
  const [chStates,   setChStates]   = useState({});
  const [flashingOff, setFlashingOff] = useState(new Set());
  const [chPending,  setChPending]  = useState(new Set());
  const [allOn,      setAllOn]      = useState(false);

  const triggerOffFlash = (key) => {
    setFlashingOff(s => new Set([...s, key]));
    setTimeout(() => setFlashingOff(s => { const n = new Set(s); n.delete(key); return n; }), 2500);
  };
  // Speak (record → send / type → speak) is shared with the industrial Ops console.
  const { speakText, setSpeakText, speaking, recording, micError,
          toggleRecord, speakTyped } = useSpeak();

  useEffect(() => {
    apiFetch('/ui/devices/status')
      .then(r => r.json())
      .then(d => setChannels(d.relay_channels || []))
      .catch(() => {});
  }, []);

  const fireSiren = async (on) => {
    setSirenOn(on);
    setChPending(s => new Set([...s, 'siren']));
    try {
      await apiFetch(on ? '/controls/siren' : '/controls/stop', { method: 'POST' });
      if (!on) triggerOffFlash('siren');
    } catch {
      setSirenOn(!on);
    } finally {
      setChPending(s => { const n = new Set(s); n.delete('siren'); return n; });
    }
  };

  const fireSpeak = () => speakTyped();

  const fireChannel = async (ch, on) => {
    setChStates(s => ({ ...s, [ch]: on }));
    setChPending(s => new Set([...s, ch]));
    try {
      await apiFetch(`/controls/relay/${ch}/` + (on ? 'on' : 'off'), { method: 'POST' });
      if (!on) triggerOffFlash(ch);
    } catch {
      setChStates(s => ({ ...s, [ch]: !on }));
    } finally {
      setChPending(s => { const n = new Set(s); n.delete(ch); return n; });
    }
  };

  const fireAll = async (on) => {
    await Promise.allSettled([
      fireSiren(on),
      ...channels.map(ch => fireChannel(ch.channel, on)),
    ]);
    setAllOn(on);
    if (!on) triggerOffFlash('all');
  };

  return (
    <>
      {/* ── All Devices ── */}
      <p className="dash-sheet-section-label">⚡ All Devices</p>
      <div className="dash-toggle-list">
        <div className="dash-toggle-row dash-toggle-row--all">
          <span className="dash-toggle-icon">{allOn ? '✅' : '⛔'}</span>
          <span className="dash-toggle-label">All Devices</span>
          <span className={`dash-toggle-state${allOn ? ' dash-toggle-state--on' : ''}`}>{allOn ? 'On' : 'Off'}</span>
          <button
            className={`dash-toggle-switch${allOn ? ' dash-toggle-switch--on' : ''}`}
            onClick={() => fireAll(!allOn)}
            aria-pressed={allOn}>
            <span className="dash-toggle-thumb" />
          </button>
        </div>
      </div>

      {/* ── Play / Test ── */}
      <p className="dash-sheet-section-label" style={{ marginTop: 14 }}>▶️ Play / Test</p>
      <div className="dash-toggle-list">
        <div className="dash-toggle-row">
          <span className="dash-toggle-icon">{sirenOn ? '🔊' : '🔇'}</span>
          <span className="dash-toggle-label">Speaker</span>
          {chPending.has('siren')
            ? <span className="dash-toggle-loader"><span className="dash-toggle-spinner" /></span>
            : <span className={`dash-toggle-state${sirenOn ? ' dash-toggle-state--on' : ''}`}>{sirenOn ? 'On' : 'Off'}</span>
          }
          <button
            className={`dash-toggle-switch${sirenOn ? ' dash-toggle-switch--on' : ''}${chPending.has('siren') ? ' dash-toggle-switch--pending' : ''}`}
            onClick={() => fireSiren(!sirenOn)}
            disabled={chPending.has('siren')}
            aria-pressed={sirenOn}>
            <span className="dash-toggle-thumb" />
          </button>
        </div>
        {channels.filter(ch => !['motor','strobe'].includes(ch.type)).map(ch => {
          const isOn = chStates[ch.channel] ?? false;
          const isPending = chPending.has(ch.channel);
          const icon = RELAY_TYPE_ICON[ch.type] || '🔌';
          return (
            <div key={ch.channel} className="dash-toggle-row">
              <span className="dash-toggle-icon">{icon}</span>
              <span className="dash-toggle-label">{ch.name}</span>
              {isPending
                ? <span className="dash-toggle-loader"><span className="dash-toggle-spinner" /></span>
                : <span className={`dash-toggle-state${isOn ? ' dash-toggle-state--on' : ''}`}>{isOn ? 'On' : 'Off'}</span>
              }
              <button
                className={`dash-toggle-switch${isOn ? ' dash-toggle-switch--on' : ''}${isPending ? ' dash-toggle-switch--pending' : ''}`}
                onClick={() => fireChannel(ch.channel, !isOn)}
                disabled={isPending}
                aria-pressed={isOn}>
                <span className="dash-toggle-thumb" />
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Motor & Strobe ── */}
      {channels.some(ch => ['motor','strobe'].includes(ch.type)) && <>
        <p className="dash-sheet-section-label" style={{ marginTop: 14 }}>⚙️ Motor & Strobe</p>
        <div className="dash-toggle-list">
          {channels.filter(ch => ['motor','strobe'].includes(ch.type)).map(ch => {
            const isOn = chStates[ch.channel] ?? false;
            const isPending = chPending.has(ch.channel);
            const icon = RELAY_TYPE_ICON[ch.type] || '⚙️';
            return (
              <div key={ch.channel} className="dash-toggle-row">
                <span className="dash-toggle-icon">{icon}</span>
                <span className="dash-toggle-label">{ch.name}</span>
                {isPending
                  ? <span className="dash-toggle-loader"><span className="dash-toggle-spinner" /></span>
                  : <span className={`dash-toggle-state${isOn ? ' dash-toggle-state--on' : ''}`}>{isOn ? 'On' : 'Off'}</span>
                }
                <button
                  className={`dash-toggle-switch${isOn ? ' dash-toggle-switch--on' : ''}${isPending ? ' dash-toggle-switch--pending' : ''}`}
                  onClick={() => fireChannel(ch.channel, !isOn)}
                  disabled={isPending}
                  aria-pressed={isOn}>
                  <span className="dash-toggle-thumb" />
                </button>
              </div>
            );
          })}
        </div>
      </>}

      {/* ── Speak ── */}
      <p className="dash-sheet-section-label" style={{ marginTop: 20 }}>🎙 Speak</p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <button
          onClick={toggleRecord}
          disabled={speaking}
          style={{
            flex: 1, padding: '11px 0', borderRadius: 10, fontSize: 15, fontWeight: 700,
            background: recording ? '#e53935' : 'var(--surface-alt)',
            color: recording ? '#fff' : 'var(--text)',
            border: recording ? 'none' : '1.5px solid var(--border)',
            cursor: speaking ? 'not-allowed' : 'pointer',
            opacity: speaking ? 0.5 : 1,
            transition: 'background 0.2s',
          }}>
          {recording ? '⏹ Stop & Send' : '🎙 Record Voice'}
        </button>
      </div>
      {micError && (
        <p style={{ fontSize: 12, color: '#e53935', margin: '-2px 0 8px', lineHeight: 1.4 }}>
          ⚠ {micError}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Or type text to speak aloud…"
          value={speakText}
          onChange={e => setSpeakText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && fireSpeak()}
          disabled={recording}
          style={{
            flex: 1, minWidth: 0, padding: '9px 12px', borderRadius: 8, fontSize: 13,
            border: '1.5px solid var(--border)', background: 'var(--surface-alt)',
            color: 'var(--text)', outline: 'none', opacity: recording ? 0.4 : 1,
          }}
        />
        <button
          onClick={fireSpeak}
          disabled={speaking || recording || !speakText.trim()}
          style={{
            padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700,
            background: 'var(--primary)', color: 'var(--btn-primary-text, #000)',
            border: 'none', cursor: 'pointer',
            opacity: (speaking || recording || !speakText.trim()) ? 0.5 : 1,
            whiteSpace: 'nowrap',
          }}>
          {speaking ? '…' : '🔊 Speak'}
        </button>
      </div>
    </>
  );
}

// onDone: called after a pause / resume (the sheet closes; the page stays).
export function PauseDevicesSection({ paused, remaining, onChanged, onDone }) {
  const [busy, setBusy] = useState(false);

  const setPause = async (duration) => {
    setBusy(true);
    try {
      await apiFetch('/controls/devices/pause', { method: 'POST', json: { duration } });
      onChanged();
      announcePauseChange();
    } finally { setBusy(false); onDone?.(); }
  };

  const resume = async () => {
    setBusy(true);
    try {
      await apiFetch('/controls/devices/resume', { method: 'POST' });
      onChanged();
      announcePauseChange();
    } finally { setBusy(false); onDone?.(); }
  };

  return (
    <>
      {paused ? (
        <>
          <div className="dash-pause-countdown">{fmtRemaining(remaining)}</div>
          <p className="dash-sheet-sub">Relays/speakers won't trigger. Alerts still fire normally.</p>
          <button className="dash-resume-btn" disabled={busy} onClick={resume}>▶️ Resume Now</button>
        </>
      ) : (
        <>
          <p className="dash-sheet-sub">Relays/speakers won't trigger during pause. Alerts still fire normally.</p>
          <div className="dash-sheet-grid">
            {PAUSE_DURATIONS.map(d => (
              <button key={d.key} disabled={busy} onClick={() => setPause(d.key)}>{d.label}</button>
            ))}
          </div>
        </>
      )}
    </>
  );
}
